'use client'

import { motion } from 'framer-motion'
import { type ComponentProps, type ReactNode, useLayoutEffect, useRef, useState } from 'react'

import type { Input } from '../_base/Input'

import { cn } from '../../utils/cn'
import { Button } from '../_base/Button'
import { TextIconSwapAnimation } from '../feedback/TextIconSwapAnimation'
import { InputWithExtraContent } from './InputWithExtraContent'

const BUTTON_PADDING = 20
const INLINE_PADDING = 12

export interface InputWithActionProps extends Omit<ComponentProps<typeof Input>, 'children'> {
  onActionButtonClick: () => void
  actionButtonLabel: string
  isActionButtonDisabled?: boolean
  isActionButtonHidden?: boolean
  icon?: ReactNode
  actionVariant?: 'button' | 'inline'
}

export function InputWithAction({
  onActionButtonClick,
  actionButtonLabel,
  isActionButtonDisabled = false,
  isActionButtonHidden = false,
  icon,
  actionVariant = 'button',
  ...inputProps
}: InputWithActionProps) {
  const textRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)

  useLayoutEffect(() => {
    setWidth(textRef.current?.offsetWidth ?? 0)
  }, [actionButtonLabel])

  if (isActionButtonHidden) {
    return <InputWithExtraContent extraContent={null} {...inputProps} />
  }

  const isInline = actionVariant === 'inline'

  const animatedLabel = (
    <motion.div
      animate={{ width: width || 'auto' }}
      transition={{ duration: 0.15, type: 'spring', bounce: 0 }}
    >
      <TextIconSwapAnimation animationKey={actionButtonLabel} duration={0.1}>
        <div ref={textRef} className="flex items-center gap-1">
          {icon}
          {actionButtonLabel}
        </div>
      </TextIconSwapAnimation>
    </motion.div>
  )

  const actionContent = isInline ? (
    <span
      role="button"
      tabIndex={0}
      data-testid="input-action-button"
      data-input-action
      className={cn(
        'text-info-foreground text-xs font-medium select-none',
        isActionButtonDisabled ? 'cursor-default opacity-50' : 'cursor-pointer'
      )}
      onMouseDown={e => {
        e.preventDefault()
      }}
      onClick={isActionButtonDisabled ? undefined : onActionButtonClick}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          if (!isActionButtonDisabled) onActionButtonClick()
        }
      }}
      aria-disabled={isActionButtonDisabled || undefined}
    >
      {animatedLabel}
    </span>
  ) : (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      data-testid="input-action-button"
      data-input-action
      onMouseDown={e => {
        e.preventDefault()
      }}
      onClick={onActionButtonClick}
      disabled={isActionButtonDisabled}
    >
      {animatedLabel}
    </Button>
  )

  return (
    <InputWithExtraContent
      extraContentPadding={isInline ? INLINE_PADDING : BUTTON_PADDING}
      extraContent={actionContent}
      {...inputProps}
    />
  )
}
